/**
 * AnalyticsContext.js
 * Provides a centralized context for tracking analytics events throughout the application.
 * Implements common analytics patterns and standardizes event tracking.
 */

import React, { createContext, useContext } from 'react';
import { trackEvent } from '../config/firebase';

const AnalyticsContext = createContext();

/**
 * Custom hook to access analytics functions throughout the application
 * @returns {Object} Analytics functions for event tracking
 */
export const useAnalytics = () => {
  return useContext(AnalyticsContext);
};

/**
 * Analytics Provider Component
 * Wraps the application with analytics context and provides tracking functions
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AnalyticsProvider = ({ children }) => {
  /**
   * Generic event tracking function
   * @param {string} eventName - Name of the event to track
   * @param {Object} eventParams - Additional parameters for the event
   */
  const logEvent = (eventName, eventParams = {}) => {
    trackEvent(eventName, eventParams);
  };

  /**
   * Track page views with standardized parameters
   * @param {string} pageName - Name of the page being viewed
   * @param {Object} pageParams - Additional page parameters
   */
  const logPageView = (pageName, pageParams = {}) => {
    logEvent('page_view', {
      page_title: pageName,
      ...pageParams,
    });
  };

  /**
   * Track button clicks with metadata
   * @param {string} buttonName - Name/identifier of the button
   * @param {Object} buttonParams - Additional button parameters
   */
  const logButtonClick = (buttonName, buttonParams = {}) => {
    logEvent('button_click', {
      button_name: buttonName,
      ...buttonParams,
    });
  };

  /**
   * Track form submissions with success/failure status
   * @param {string} formName - Name/identifier of the form
   * @param {Object} formParams - Form submission parameters
   */
  const logFormSubmission = (formName, formParams = {}) => {
    logEvent('form_submission', {
      form_name: formName,
      ...formParams,
    });
  };

  /**
   * Track authentication events with method and status
   * @param {string} eventType - Type of auth event (login, signup, etc.)
   * @param {Object} params - Authentication event parameters
   */
  const logAuthEvent = (eventType, params = {}) => {
    logEvent(`auth_${eventType}`, {
      method: params.method || 'email',
      ...params,
    });
  };

  const value = {
    logEvent,
    logPageView,
    logButtonClick,
    logFormSubmission,
    logAuthEvent,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
